import type { ApiErrorBody, AuthResponseDto } from '@vmt/shared';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody | null,
  ) {
    super(body?.message ?? `Anfrage fehlgeschlagen (${status})`);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, e.g. "players.0.agentId". */
  get details(): Record<string, string[]> {
    return this.body?.details ?? {};
  }
}

type Listener = (auth: AuthResponseDto | null) => void;

let accessToken: string | null = null;
let refreshPromise: Promise<AuthResponseDto | null> | null = null;
const listeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return accessToken;
}

/** Updates the in-memory session (the refresh token lives in an httpOnly cookie). */
export function setSession(auth: AuthResponseDto | null): void {
  accessToken = auth?.accessToken ?? null;
  listeners.forEach((l) => l(auth));
}

export function onSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function doRefresh(): Promise<AuthResponseDto | null> {
  const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    setSession(null);
    return null;
  }
  const auth = (await res.json()) as AuthResponseDto;
  setSession(auth);
  return auth;
}

/**
 * Refreshes the access token. Concurrent calls share one request; across tabs the Web Locks API
 * serializes refreshes so a rotated cookie is never reused (which would trigger reuse detection).
 */
export function refreshSession(): Promise<AuthResponseDto | null> {
  refreshPromise ??= (async () => {
    try {
      if (typeof navigator !== 'undefined' && 'locks' in navigator) {
        return await navigator.locks.request('vmt-refresh', () => doRefresh());
      }
      return await doRefresh();
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '')
      url.searchParams.set(key, String(value));
  }

  const send = () => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      credentials: 'include',
      signal: options.signal,
    });
  };

  let res = await send();
  if (res.status === 401 && accessToken && !path.startsWith('/auth/')) {
    const refreshed = await refreshSession();
    if (refreshed) res = await send();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const details = Object.values(error.details).flat();
    return details.length && error.status === 400 ? details.join(' · ') : error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}
